import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";

import { RouteLocation } from "../routing/useLocationPath";

export function RouteCanvas({ children, routeKey }: { children: ReactNode; routeKey: string }) {
  const canvasRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    canvasRef.current?.focus({ preventScroll: true });
  }, []);

  return (
    <div ref={canvasRef} className="route-canvas" data-route={routeKey} tabIndex={-1}>
      {children}
    </div>
  );
}

// Keep the completed route mounted while the destination bootstraps through its
// existing loaders. Hidden destination effects still run; no duplicate fetch,
// cached financial data or captured DOM is used. Commit once, before paint.
const DocumentRouteState = createContext<{ active: boolean; ready(): void }>({active:true,ready:()=>{}});
export function useDocumentRouteReady(ready: boolean) {
  const route = useContext(DocumentRouteState);
  useLayoutEffect(() => { if (ready) route.ready(); }, [ready,route]);
  return route.active;
}
export function DocumentRouteCanvas({ routeKey, locationUrl, defer, fallback, children }: { routeKey: string; locationUrl: string; defer: boolean; fallback?: ReactNode; children: ReactNode }) {
  const [current,setCurrent] = useState(() => ({key:defer && fallback ? `initial:${routeKey}` : routeKey,url:locationUrl,view:defer && fallback ? fallback : children}));
  const incoming = useRef({key:routeKey,url:locationUrl,view:children});
  incoming.current = {key:routeKey,url:locationUrl,view:children};
  const commit = useCallback((key:string) => setCurrent(previous => key !== incoming.current.key || previous.key === key ? previous : incoming.current),[]);
  if ((!defer || current.key === routeKey) && (current.key !== routeKey || current.url !== locationUrl)) {
    setCurrent({key:routeKey,url:locationUrl,view:children});
  }
  const pending = defer && current.key !== routeKey;
  const entries = pending ? [current,{key:routeKey,url:locationUrl,view:children}] : [{key:routeKey,url:locationUrl,view:children}];
  return <>{entries.map(entry => {
    const active = !pending || entry.key === current.key;
    return <DocumentRouteState.Provider key={entry.key} value={{active,ready:entry.key === routeKey ? ()=>commit(entry.key) : ()=>{}}}>
      <RouteLocation.Provider value={entry.key.startsWith("initial:") ? null : entry.key === routeKey ? locationUrl : entry.url}>
        <div className="route-view" hidden={!active} inert={pending} aria-busy={active && pending || undefined}>{entry.view}</div>
      </RouteLocation.Provider>
    </DocumentRouteState.Provider>;
  })}{pending ? <span className="route-navigation-progress" role="status" aria-label={/^\/(invoices|estimates)\//.test(routeKey) ? "Opening document" : "Opening page"}><span className="visually-hidden">Opening…</span></span> : null}</>;
}
